import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, type Me } from './api.js';

export interface Resource<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => void;
  /** Replace the data locally (after a mutation) without refetching. */
  set: (next: T) => void;
}

/**
 * Load something once per `deps` change, with a manual reload. `load` may be an inline closure;
 * only `deps` decide when it re-runs.
 */
export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[]): Resource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the caller's cache key; tick forces a reload
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    loadRef
      .current()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, set: setData };
}

/** Run `fn` every `ms` milliseconds while `ms` is a number; pass null to pause. */
export function useInterval(fn: () => void, ms: number | null): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const id = window.setInterval(() => ref.current(), ms);
    return () => window.clearInterval(id);
  }, [ms]);
}

/** Track an async action: pending flag + last error, for buttons and forms. */
export function useAction<Args extends unknown[]>(fn: (...args: Args) => Promise<void>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const run = useCallback(
    async (...args: Args) => {
      setPending(true);
      setError(undefined);
      try {
        await fn(...args);
      } catch (e) {
        setError(e);
      } finally {
        setPending(false);
      }
    },
    [fn],
  );
  return { run, pending, error, clearError: () => setError(undefined) };
}

/** Who is signed in. `error` is the 401 (or other failure) when nobody is. */
export function useMe(): Resource<Me> {
  return useResource(() => api.me(), []);
}

export const MeContext = createContext<Me | undefined>(undefined);

/** The signed-in user, inside a protected route. */
export function useCurrentUser(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error('useCurrentUser must be used inside a protected route');
  return me;
}

export async function signOut(): Promise<void> {
  try {
    await api.logout();
  } finally {
    window.location.assign('/sign-in');
  }
}
